// src/web/PerceptualResearchInfrastructure.jsx

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import PageTracker from '../../components/tracking/PageTracker';
import ScrollTracker from '../../components/tracking/ScrollTracker';
import AVIcon from '../../components/icons/AllVoicedIcons';
import PerceptualResearchInfrastructureFAQ from '../../components/faq/PerceptualResearchInfrastructureFAQ';

export default function PerceptualResearchInfrastructure() {
  return (
    <main className="service-landing-page perceptual-research-infrastructure-page">
      <Helmet>
        <title>Perceptual Research Infrastructure | All-Voiced</title>
        <meta
          name="description"
          content="Design and coordinate structured perceptual workflows for clinical studies, voice research, psychophysical experiments, multicenter projects, and other studies requiring human perceptual evaluation."
        />
      </Helmet>

      <PageTracker pageName="PerceptualResearchInfrastructure" />

      <section className="service-landing-hero">
        <span className="services-eyebrow">
          Perceptual Research Infrastructure
        </span>

        <h1>
          Design and Coordinate Structured Perceptual Workflows for Clinical and
          Research Studies
        </h1>

        <p>
          All-Voiced helps researchers, clinical teams, institutions, and
          technology partners design, configure, and coordinate the
          perceptual layer of studies requiring structured human evaluation
          of voice.
        </p>

        <div className="services-hero-cta web-cta-row">
          <Link
            className="web-button web-button--primary"
            to="/meet"
          >
            Discuss a study
          </Link>

          <Link
            className="web-button web-button--secondary"
            to="/perceptual-data-annotation"
          >
            Explore data annotation
          </Link>
        </div>

        <div className="service-landing-panel">
          <span>Perceptual Workflow Design</span>
          <span className="service-landing-separator">•</span>
          <span>Evaluator Workflows</span>
          <span className="service-landing-separator">•</span>
          <span>Quality Monitoring</span>
          <span className="service-landing-separator">•</span>
          <span>Structured Data</span>
        </div>
      </section>

      <section className="service-landing-section service-landing-intro">
        <span className="services-label">
          Why perceptual research infrastructure?
        </span>

        <h2>
          Human perceptual evaluation is often a critical study layer
        </h2>

        <p>
          Many studies depend on structured human judgments even when the
          main research question concerns treatment effectiveness, clinical
          change, biomarker development, technology evaluation, longitudinal
          progression, methodological comparison, cross-cultural variation,
          or educational outcomes.
        </p>

        <p>
          Building this layer requires more than displaying samples and
          collecting ratings. The study must define the perceptual protocol,
          sample presentation, evaluator workflow, blinding conditions,
          completion monitoring, and required outputs.
        </p>

        <div className="service-landing-grid four">
          <div className="service-landing-card">
            <AVIcon icon="research" size={28} color="#209385" />

            <h3>Protocol implementation</h3>

            <p>
              Translate an approved or proposed research protocol into a
              structured perceptual evaluation workflow.
            </p>
          </div>

          <div className="service-landing-card">
            <AVIcon icon="team" size={28} color="#209385" />

            <h3>Evaluator coordination</h3>

            <p>
              Organize evaluator groups, sample allocations, phases, 
              and repeated evaluations according to the study design.
            </p>
          </div>

          <div className="service-landing-card">
            <AVIcon icon="lock" size={28} color="#209385" />

            <h3>Controlled presentation</h3>

            <p>
              Configure instructions, sample labels, ordering, access, and
              blinding conditions where required by the protocol.
            </p>
          </div>

          <div className="service-landing-card">
            <AVIcon icon="dashboard" size={28} color="#209385" />

            <h3>Structured research outputs</h3>

            <p>
              Capture perceptual responses and agreed operational outputs in
              structured formats.
            </p>
          </div>
        </div>
      </section>

      <section className="service-landing-section">
        <span className="services-label">
          Supported workflows
        </span>

        <h2>
          What the infrastructure can support
        </h2>

        <p>
          The exact implementation depends on the research question,
          protocol, governance requirements, available samples, evaluator
          workflow, and agreed outputs.
        </p>

        <div className="service-landing-grid">
          <div className="service-landing-card">
            <h3>Perceptual protocol configuration</h3>

            <p>
              Translate the study protocol into task logic, conditions,
              sequencing, instructions, and evaluation phases.
            </p>
          </div>

          <div className="service-landing-card">
            <h3>Task and scale configuration</h3>

            <p>
              Use supported or configurable paradigms such as VAS, 2AFC,
              categorization, ranking, sorting, matching, CAPE-V-related
              workflows, and other appropriate task models, depending on
              methodological and technical feasibility.
            </p>
          </div>

          <div className="service-landing-card">
            <h3>Evaluator workflows</h3>

            <p>
              Coordinate evaluators, groups, phases, sample allocations, 
              repeated evaluations, and blinded conditions where supported 
              by the agreed implementation.
            </p>
          </div>

          <div className="service-landing-card">
            <h3>Sample presentation workflows</h3>

            <p>
              Structure how voice samples are presented, ordered, grouped,
              labelled, and allocated within the perceptual evaluation
              workflow.
            </p>
          </div>

          <div className="service-landing-card">
            <h3>Quality monitoring</h3>

            <p>
              Monitor completion, response patterns, disagreement,
              variability, and reliability-related indicators where supported
              by the study design and implementation.
            </p>
          </div>

          <div className="service-landing-card">
            <h3>Structured data and exports</h3>

            <p>
              Produce structured response data and agreed exports for
              downstream analysis, reporting, evaluation, or publication.
              Dashboards and reports depend on the implementation scope.
            </p>
          </div>
        </div>
      </section>

      <section className="service-landing-section service-landing-callout">
        <span className="services-label">
          Study contexts
        </span>

        <h2>
          Relevant beyond psychophysical experiments
        </h2>

        <p>
          Perceptual evaluation may be the primary research object, or it may
          be one structured evidence layer within a broader study.
        </p>

        <div className="service-landing-grid">
          <div className="service-landing-card">
            <h3>Clinical intervention studies</h3>

            <p>
              Coordinate blinded or multirater evaluation of pre- and
              post-treatment voice samples using predefined protocols.
            </p>
          </div>

          <div className="service-landing-card">
            <h3>Longitudinal and multicenter research</h3>

            <p>
              Support repeated phases, multiple evaluator groups, and
              coordinated workflows across sites where governance and
              institutional responsibilities allow.
            </p>
          </div>

          <div className="service-landing-card">
            <h3>Protocol validation and methodology</h3>

            <p>
              Compare perceptual tasks, rating methods, instructions, or
              evaluator workflows under structured conditions.
            </p>
          </div>

          <div className="service-landing-card">
            <h3>Vocal biomarkers and technology evaluation</h3>

            <p>
              Generate structured human perceptual reference data that may
              support interpretation, benchmarking, or comparison with
              signal-derived and model-derived outputs.
            </p>
          </div>

          <div className="service-landing-card">
            <h3>Educational research</h3>

            <p>
              Study learning processes, evaluator development, assessment
              models, and structured listening workflows in educational
              contexts.
            </p>
          </div>

          <div className="service-landing-card">
            <h3>Cross-cultural and multilingual studies</h3>

            <p>
              Coordinate perceptual workflows across languages, contexts, or
              evaluator populations when the study protocol requires it.
            </p>
          </div>
        </div>
      </section>

      <section className="service-landing-section service-landing-highlight">
        <span className="services-label">
          Research workflow architecture
        </span>

        <h2>
          From research protocol to structured perceptual evidence
        </h2>

        <p>
          All-Voiced operationalizes the perceptual layer defined by the
          study protocol, connecting samples, evaluators, tasks, quality
          monitoring, and structured outputs.
        </p>

        <div className="service-landing-panel">
          <span>Research protocol</span>
          <span className="service-landing-separator">↓</span>
          <span>Perceptual workflow design</span>
          <span className="service-landing-separator">↓</span>
          <span>Sample presentation</span>
          <span className="service-landing-separator">↓</span>
          <span>Evaluator workflow</span>
          <span className="service-landing-separator">↓</span>
          <span>Task and scale configuration</span>
          <span className="service-landing-separator">↓</span>
          <span>Quality monitoring</span>
          <span className="service-landing-separator">↓</span>
          <span>Structured response data</span>
          <span className="service-landing-separator">↓</span>
          <span>Research export</span>
        </div>
      </section>

      <section className="service-landing-section">
        <span className="services-label">
          Flexible implementation
        </span>

        <h2>
          From focused evaluations to coordinated research workflows
        </h2>

        <p>
          An implementation may begin with one rating task, a small
          evaluator panel, a pre-post comparison, or a pilot dataset. It may
          extend to repeated phases, multiple evaluator groups, longitudinal
          evaluation, multicenter coordination, or broader task batteries.
        </p>

        <p>
          The appropriate workflow depends not only on the task, but also on
          evaluator selection, instructions, allocation, blinding, repeated
          evaluations, and how disagreement and variability will be
          represented.
        </p>

        <p>
          The goal is to configure a methodologically appropriate and
          operationally feasible perceptual workflow. Training, calibration,
          qualification, and reliability requirements must be defined for
          each study and are not established automatically through
          participation.
        </p>
      </section>

      <section className="service-landing-section">
        <span className="services-label">
          Data and outputs
        </span>

        <h2>
          From perceptual judgments to structured research data
        </h2>

        <p>
          Depending on the protocol and implementation scope, outputs may
          include raw response data, structured ratings, completion records,
          sample identifiers, study-specific evaluator identifiers, aggregate
          or consensus values, variability summaries, reliability-related
          indicators, dashboards, reports, or research exports.
        </p>

        <p>
          The research team remains responsible for determining how these
          outputs are analysed and interpreted unless analysis support is
          explicitly included in the agreed scope.
        </p>
      </section>

      <section className="service-landing-section service-landing-callout">
        <span className="services-label">
          How we work
        </span>

        <h2>
          A research-oriented implementation process
        </h2>

        <div className="services-steps">
          <div className="services-step">
            <span className="services-step-number">1</span>

            <div>
              <h3>Understand the study</h3>

              <p>
                Clarify the research question, approved or proposed
                protocol, samples, evaluator population, timeline, outputs,
                and governance constraints.
              </p>
            </div>
          </div>

          <div className="services-step">
            <span className="services-step-number">2</span>

            <div>
              <h3>Define the perceptual workflow</h3>

              <p>
                Specify tasks, scales, sample allocation, blinding, study
                phases, quality monitoring, and required outputs.
              </p>
            </div>
          </div>

          <div className="services-step">
            <span className="services-step-number">3</span>

            <div>
              <h3>Configure and validate</h3>

              <p>
                Configure the study environment, test the workflow, review
                sample presentation, and verify the perceptual workflow
                before launch.
              </p>
            </div>
          </div>

          <div className="services-step">
            <span className="services-step-number">4</span>

            <div>
              <h3>Run, monitor, and export</h3>

              <p>
                Support or operate the agreed perceptual workflow, monitor
                completion and defined quality indicators, and deliver the
                agreed structured outputs.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="service-landing-section service-landing-resources">
        <span className="services-label">
          Related service line
        </span>

        <h2>
          Research workflows and data annotation are connected
        </h2>

        <p>
          Perceptual Research Infrastructure starts from a research protocol
          and a study-specific perceptual workflow. Perceptual Data Annotation
          starts from a dataset and the need to generate a structured
          human-perceptual layer. Some projects require both.
        </p>

        <div className="service-landing-resource-card">
          <div>
            <h3>Perceptual Data Annotation</h3>

            <p>
              Explore the dataset-centered service line for projects focused
              on generating structured human-perceptual data for a voice
              dataset.
            </p>
          </div>

          <Link
            className="web-button web-button--secondary"
            to="/perceptual-data-annotation"
          >
            Explore data annotation
          </Link>
        </div>
      </section>

      <PerceptualResearchInfrastructureFAQ />

      <section className="services-final-cta">
        <h2>
          Plan the perceptual layer of your study
        </h2>

        <p>
          Discuss your research question, study design, samples, evaluator
          needs, and required outputs to identify an appropriate perceptual
          workflow.
        </p>

        <div className="services-hero-cta web-cta-row">
          <Link
            className="web-button web-button--primary"
            to="/meet"
          >
            Discuss a study
          </Link>

          <Link
            className="web-button web-button--secondary"
            to="/perceptual-data-annotation"
          >
            Explore data annotation
          </Link>
        </div>
      </section>

      <ScrollTracker pageName="PerceptualResearchInfrastructure" />
    </main>
  );
}